import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, MessageSquare, X } from "lucide-react";

import { getSmsConsent, setSmsConsent } from "../api";

const PRIVACY_URL = "https://clubbot.ai/privacy.html";
const TERMS_URL = "https://clubbot.ai/terms.html";

/**
 * SMS opt-in screen. Collects a mobile number + explicit, affirmative consent to
 * receive recurring automated texts — with the disclosures required for A2P
 * 10DLC / CTIA (program name, message types, "msg & data rates may apply",
 * frequency, STOP/HELP, and links to Privacy + Terms).
 */
export function SmsOptIn({ onClose }: { onClose: () => void }) {
  const [phone, setPhone] = useState("");
  const [agreed, setAgreed] = useState(false); // unchecked by default — opt-in must be affirmative
  const [consented, setConsented] = useState(false); // current server state
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSmsConsent()
      .then((s) => {
        if (cancelled) return;
        if (s.phone_e164) setPhone(s.phone_e164);
        setConsented(s.consent);
      })
      .catch(() => undefined);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      cancelled = true;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const canSubmit = agreed && phone.trim().length >= 7 && !busy;

  async function turnOn() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const s = await setSmsConsent({ phone: phone.trim(), consent: true });
      setConsented(s.consent);
      if (s.phone_e164) setPhone(s.phone_e164);
      setSavedAt(true);
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function turnOff() {
    setBusy(true);
    setError(null);
    try {
      const s = await setSmsConsent({ phone: phone.trim(), consent: false });
      setConsented(s.consent);
      setAgreed(false);
      setSavedAt(false);
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 bg-bg overflow-y-auto cs-modal-overlay">
      <header className="sticky top-0 z-10 bg-bg/95 backdrop-blur border-b border-ink/10">
        <div className="max-w-lg mx-auto px-5 py-3 flex items-center justify-between">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55">
            Text notifications
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 -mr-1 flex items-center justify-center text-ink/55 hover:text-ink cs-tap"
          >
            <X size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-5 py-8 pb-20">
        <div className="w-12 h-12 rounded-lg bg-ink flex items-center justify-center mb-4">
          <MessageSquare size={22} className="text-accent" />
        </div>
        <h1 className="font-display font-bold text-3xl tracking-tight mb-2">
          Get game updates by text
        </h1>
        <p className="text-ink/65 leading-relaxed mb-6">
          Turn on texts from ClubBot for <strong>game reminders, RSVP &amp; schedule
          updates, and pickup invites</strong> in your clubs. You can also just text
          ClubBot back to RSVP or ask a question.
        </p>

        {consented && !savedAt && (
          <div className="mb-5 flex items-center gap-2 border border-success/40 bg-success/10 rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-success">
            <Check size={14} /> Text updates are on
          </div>
        )}
        {savedAt && (
          <div className="mb-5 flex items-center gap-2 border border-success/40 bg-success/10 rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-success">
            <Check size={14} /> You're signed up for text updates
          </div>
        )}

        <label className="block mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55">
            Mobile number
          </span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 (555) 123-4567"
            className="mt-1.5 w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
          />
        </label>

        <label className="flex gap-3 items-start mb-5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 w-4 h-4 shrink-0 accent-accent cs-tap"
          />
          <span className="text-xs text-ink/65 leading-relaxed">
            By checking this box, I agree to receive recurring automated text
            messages (game reminders, RSVP &amp; schedule updates, and pickup
            invites) from ClubBot at the mobile number above. Consent is not a
            condition of any purchase. Msg &amp; data rates may apply. Message
            frequency varies. Reply <strong>STOP</strong> to unsubscribe or{" "}
            <strong>HELP</strong> for help. See ClubBot's{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-accent-dim decoration-2 underline-offset-2"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-accent-dim decoration-2 underline-offset-2"
            >
              Terms
            </a>
            .
          </span>
        </label>

        {error && <div className="text-sm text-[#C84B31] mb-4">{error}</div>}

        <button
          onClick={() => void turnOn()}
          disabled={!canSubmit}
          className="w-full px-5 py-3 bg-accent text-ink font-display font-bold text-base rounded-lg cs-tap disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105"
        >
          {busy ? "Saving…" : consented ? "Update text settings" : "Turn on text updates"}
        </button>

        {consented && (
          <button
            onClick={() => void turnOff()}
            disabled={busy}
            className="w-full mt-3 font-mono text-[11px] uppercase tracking-wider text-ink/45 hover:text-ink cs-tap py-2"
          >
            Turn off text updates
          </button>
        )}
      </main>
    </div>,
    document.body,
  );
}
